import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { TFunction } from "i18next";

import {
  Badge,
  Button,
  EmptyState,
  Input,
  Pagination,
  SectionHeader,
  ViewToggle,
  SwitchTrack,
} from "../../../components/ui";
import type { CustomAlertProps } from "../../../components/base/CustomAlert";
import { resetLastProcessedCoordinates } from "../../../components/base/MapPicker";
import { useDarkMode } from "../../../context/ThemeContext";
import { useCentinelaGeofenceSuggestionJob } from "../../../features/centinela/queries";
import { useRequestCentinelaGeofenceSuggestions } from "../../../features/centinela/mutations";
import type { CentinelaGeofenceSuggestionsStart } from "../../../features/centinela/types";
import { useGeofencesList } from "../../../features/geofences/queries";
import { useRoutesList } from "../../../features/routes/queries";
import { qk } from "../../../lib/queryKeys";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import {
  createGeofence,
  deleteGeofence,
  updateGeofence,
  type Geofence,
} from "../../../store/admin/geofencesStore";
import { useStatusesStore } from "../../../store/admin/statusesStore";
import { panelThemeClasses } from "../../../theme/themeClasses";
import { normalizeStatusId } from "../../../utils/getStatusEntity";
import { GeofenceModal } from "../../admin/components/sections/geofences/components/GeofenceModal";
import { useGeofenceForm } from "../../admin/components/sections/geofences/hooks/useGeofenceForm";
import type { ThemeClasses } from "../../admin/components/sections/geofences/types";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import RefreshSectionButton from "../components/RefreshSectionButton";
import TabLoader from "../components/TabLoader";
import FilterCombobox from "../components/FilterCombobox";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import GeofenceMapModal from "../components/GeofenceMapModal";
import ResponsiveRow, { ResponsiveHeader } from "../components/ResponsiveRow";
import { isDemoRecord } from "../services/centinelaMocks";
import {
  mapCentinelaGeofenceCorridor,
  mapCentinelaGeofenceSuggestions,
} from "../services/mapCentinelaGeofenceSuggestions";
import { GEOFENCE_TYPE_META } from "../theme";
import type { GeofenceSuggestion, GeofenceSuggestionConfidence } from "../types";

const ITEMS_PER_PAGE = 6;
const GEOFENCE_GRID = "lg:grid-cols-[1fr_140px_120px_80px_80px_60px]";
const SHAPE_FILTERS = ["all", "circular", "polygonal"] as const;
const STATUS_FILTERS = ["all", "active", "inactive"] as const;
const TYPE_FILTERS = [
  "all",
  "warehouse",
  "customer",
  "authorizedStop",
  "restrictedArea",
  "route",
] as const;

const CONFIDENCE_CHIP = {
  alta: "border-success/30 bg-success/10 text-success",
  media: "border-warning/30 bg-warning/10 text-warning",
  baja: "border-line bg-subtle text-content-muted",
} as const satisfies Record<GeofenceSuggestionConfidence, string>;

const unitsInsideOf = (geofence: Geofence) => geofence.devices?.length ?? 0;
const alertsOf = (geofence: Geofence) => geofence.alerts?.length ?? 0;

const isLocalRecord = (id: string) => id.startsWith("local-");

const isSystemManaged = (geofence: Geofence) =>
  geofence.type === "truck_yard" || !!geofence.truck_yard_id;

const timestampToDate = (value: unknown) => {
  if (value && typeof value === "object" && "_seconds" in value) {
    return new Date((value as { _seconds: number })._seconds * 1000);
  }
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  return null;
};

export default function GeocercasSection() {
  const { t, i18n } = useTranslation("common");

  const [search, setSearch] = useState("");
  const [shapeFilter, setShapeFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionRouteId, setSuggestionRouteId] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStart, setJobStart] = useState<CentinelaGeofenceSuggestionsStart | null>(null);
  const [mapGeofence, setMapGeofence] = useState<Geofence | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Geofence | null>(null);

  const [created, setCreated] = useState<Geofence[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, boolean>>({});

  const {
    data,
    isLoading,
    isFetching: geofencesFetching,
    refetch: refetchGeofences,
  } = useGeofencesList({ limit: 200 });
  const {
    data: routesData,
    isLoading: routesLoading,
    isFetching: routesFetching,
    refetch: refetchRoutes,
  } = useRoutesList({ limit: 200 });

  const refreshSection = () => {
    void refetchGeofences();
    void refetchRoutes();
  };
  const requestSuggestions = useRequestCentinelaGeofenceSuggestions();
  const suggestionJob = useCentinelaGeofenceSuggestionJob(jobId);

  const { isDarkMode } = useDarkMode();
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((state) => state.companyId);
  const statuses = useStatusesStore((state) => state.statuses);
  const fetchStatuses = useStatusesStore((state) => state.fetchStatuses);

  const themeClasses: ThemeClasses = panelThemeClasses();

  const alertT = ((key: string) => key) as TFunction<"translation", undefined>;

  const notifyFromStore = ({ text, title, icon, onConfirm }: CustomAlertProps) => {
    if (icon === "error") {
      toast.error(text || title);
      return;
    }
    onConfirm?.();
  };

  const {
    form,
    shape,
    polygonPoints,
    setPolygonPoints,
    linearPoints,
    setLinearPoints,
    routeDistance,
    estimatedTime,
    polygonArea,
    hasRealChanges,
    handleAddressSelect,
    handleLinearPointsChange,
    handleAxisCalculated,
    handleBufferChange,
    linearAddresses,
    handleLinearAddressEdit,
    handleRouteCalculated,
    resetForm,
    prepareGeofenceData,
    defaultCircularCoordinates,
  } = useGeofenceForm({ isEdit: false, editGeofence: null });

  useEffect(() => {
    if (statuses.length === 0) fetchStatuses({ limit: 200, page: 1 });
  }, [statuses.length, fetchStatuses]);

  const invalidateGeofences = () => {
    queryClient.invalidateQueries({ queryKey: ["geofences"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  const statusIdOf = (canonical: "ACTIVE" | "INACTIVE") =>
    statuses.find(
      (status) => (normalizeStatusId(status.id) ?? status.name?.toUpperCase()) === canonical,
    )?.id ?? null;

  const geofences = useMemo(
    () => [...created, ...(data?.data ?? [])].filter((geofence) => !deletedIds.includes(geofence.id)),
    [data, created, deletedIds],
  );

  const canonicalStatusOf = (geofence: Geofence) =>
    statuses.find((status) => status.id === geofence.status_id)?.name?.toUpperCase() ??
    normalizeStatusId(geofence.status_id) ??
    "";

  const isActive = (geofence: Geofence) =>
    statusOverrides[geofence.id] ?? canonicalStatusOf(geofence) !== "INACTIVE";

  const analysis = useMemo(
    () => mapCentinelaGeofenceSuggestions(suggestionJob.data),
    [suggestionJob.data],
  );

  const routeOptions = useMemo(
    () => (routesData?.data ?? []).map((route) => ({ value: route.id, label: route.name })),
    [routesData],
  );

  const visibleSuggestions = (analysis?.suggestions ?? []).filter(
    (item) => !dismissed.includes(item.id),
  );

  const analysisRunning = requestSuggestions.isPending || Boolean(analysis?.isRunning);

  const analysisFailed =
    requestSuggestions.isError || suggestionJob.isError || Boolean(analysis?.hasError);

  const analysisErrorMessage = analysis?.errorMessage ?? null;

  const corridor =
    analysis?.corridor ?? mapCentinelaGeofenceCorridor(jobStart?.corredor ?? null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return geofences.filter((geofence) => {
      const matchesTerm =
        term.length === 0 ||
        geofence.name.toLowerCase().includes(term) ||
        (geofence.address ?? "").toLowerCase().includes(term);
      const matchesShape = shapeFilter === "all" || geofence.shape === shapeFilter;
      const matchesType = typeFilter === "all" || geofence.type === typeFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? isActive(geofence) : !isActive(geofence));

      return matchesTerm && matchesShape && matchesType && matchesStatus;
    });
  }, [geofences, search, shapeFilter, typeFilter, statusFilter, statusOverrides, statuses]);

  useEffect(() => {
    setPage(1);
  }, [search, shapeFilter, typeFilter, statusFilter, viewMode]);

  useEffect(() => {
    if (showCreateModal) form.setValue("shape", "circular");
  }, [showCreateModal, form]);

  useEffect(() => {
    if (analysis?.isReady) toast.success(t("monitoreoIa.geofences.suggestionsReady"));
  }, [analysis?.isReady, t]);

  useEffect(() => () => {
    document.body.style.overflow = "";
  }, []);

  const totalPages = Math.max(Math.ceil(filtered.length / ITEMS_PER_PAGE), 1);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const toggleStatus = async (geofence: Geofence) => {
    const next = !isActive(geofence);

    if (isLocalRecord(geofence.id)) {
      setStatusOverrides((prev) => ({ ...prev, [geofence.id]: next }));
      return;
    }

    const nextStatusId = statusIdOf(next ? "ACTIVE" : "INACTIVE");
    if (!nextStatusId) {
      toast.error(t("monitoreoIa.geofences.statusUnavailable"));
      return;
    }

    setStatusOverrides((prev) => ({ ...prev, [geofence.id]: next }));
    await updateGeofence(
      geofence.id,
      { status_id: nextStatusId },
      notifyFromStore,
      setLoadingSubmit,
      alertT,
      () => {},
    );
    invalidateGeofences();
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setLoadingSubmit(false);
    resetForm();
    resetLastProcessedCoordinates();
    document.body.style.overflow = "";
  };

  const submitGeofence = async (values: unknown) => {
    const payload = prepareGeofenceData(values);
    let saved = false;

    setLoadingSubmit(true);
    await createGeofence(
      payload,
      notifyFromStore,
      setLoadingSubmit,
      alertT,
      () => {
        saved = true;
        closeCreateModal();
      },
      form.reset,
    );

    if (saved) toast.success(t("monitoreoIa.geofences.created", { name: payload.name }));
    invalidateGeofences();
  };

  const removeGeofence = async (geofence: Geofence) => {
    setDeleteTarget(null);

    if (isLocalRecord(geofence.id)) {
      setDeletedIds((prev) => [...prev, geofence.id]);
      toast.success(t("monitoreoIa.geofences.deleted", { name: geofence.name }));
      return;
    }

    let removed = false;
    await deleteGeofence(geofence.id, notifyFromStore, setLoadingSubmit, alertT, () => {
      removed = true;
    });

    if (removed) toast.success(t("monitoreoIa.geofences.deleted", { name: geofence.name }));
    invalidateGeofences();
  };

  const acceptSuggestion = (suggestion: GeofenceSuggestion) => {
    if (
      suggestion.lat === null ||
      suggestion.lng === null ||
      suggestion.radiusMeters === null
    ) {
      toast.error(t("monitoreoIa.geofences.suggestionIncomplete"));
      return;
    }

    setDismissed((prev) => [...prev, suggestion.id]);
    setCreated((prev) => [
      {
        id: `local-geofence-${suggestion.id}-${Date.now()}`,
        name: suggestion.name,
        address: "",
        type: suggestion.type,
        shape: "circular",
        status_id: "active",
        description: suggestion.reason,
        color: suggestion.color,
        geofenceActions: [],
        notifiedUsers: [],
        coordinates: {
          latitude: suggestion.lat,
          longitude: suggestion.lng,
          radius: suggestion.radiusMeters,
        },
        alerts: [],
        devices: [],
        createdAt: { _seconds: Math.round(Date.now() / 1000), _nanoseconds: 0 },
        updatedAt: { _seconds: Math.round(Date.now() / 1000), _nanoseconds: 0 },
      } as Geofence,
      ...prev,
    ]);
    toast.success(t("monitoreoIa.geofences.created", { name: suggestion.name }));
  };

  const requestAnalysis = async () => {
    if (!suggestionRouteId || analysisRunning) return;

    setDismissed([]);
    setJobId(null);
    setJobStart(null);

    const started = await requestSuggestions
      .mutateAsync(suggestionRouteId)
      .catch(() => null);

    if (!started?.job_id) {
      toast.error(t("monitoreoIa.geofences.suggestionsFailed"));
      return;
    }

    setJobStart(started);
    setJobId(started.job_id);
    started.reusado && toast.info(t("monitoreoIa.geofences.analysisReused"));
  };

  const metaOf = (type: string) => GEOFENCE_TYPE_META[type] ?? GEOFENCE_TYPE_META.default;

  const shapeLabel = (shape: string) =>
    t(`monitoreoIa.geofences.shapes.${shape === "circular" ? "circular" : "polygonal"}`);

  const radiusOf = (geofence: Geofence) =>
    Array.isArray(geofence.coordinates)
      ? t("monitoreoIa.geofences.notApplicable")
      : `${geofence.coordinates.radius} m`;

  const coordinatesOf = (geofence: Geofence) => {
    const point = Array.isArray(geofence.coordinates)
      ? geofence.coordinates[0]
      : geofence.coordinates;
    if (!point) return t("monitoreoIa.geofences.notApplicable");
    return `${point.latitude.toFixed(4)}, ${point.longitude.toFixed(4)}`;
  };

  const dateOf = (value: unknown) => {
    const date = timestampToDate(value);
    return date ? date.toLocaleDateString(i18n.language, { dateStyle: "medium" }) : "—";
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <TabLoader label={t("monitoreoIa.geofences.loadingTab")} />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.geofences.title")}
        subtitle={t("monitoreoIa.geofences.subtitle", { count: geofences.length })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RefreshSectionButton
              onRefresh={refreshSection}
              loading={geofencesFetching || routesFetching}
            />
            <Button
              variant="dashed"
              size="sm"
              icon={<i className="ri-sparkling-line" />}
              onClick={() => setShowSuggestions(true)}
            >
              {t("monitoreoIa.geofences.aiSuggestions")}
            </Button>
            <Button size="sm" icon={<i className="ri-add-line" />} onClick={openCreateModal}>
              {t("monitoreoIa.geofences.new")}
            </Button>
          </div>
        }
        filters={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-row sm:items-center sm:gap-2.5">
            <FilterCombobox
              id="geocercas-filter-1"
              value={shapeFilter}
              onChange={setShapeFilter}
              options={SHAPE_FILTERS.map((value) => ({
                value,
                label: t(`monitoreoIa.geofences.shapeFilters.${value}`),
              }))}
              containerClassName="w-full sm:w-44"
            />
            <FilterCombobox
              id="geocercas-filter-2"
              value={typeFilter}
              onChange={setTypeFilter}
              options={TYPE_FILTERS.map((value) => ({
                value,
                label: t(`monitoreoIa.geofences.typeFilters.${value}`),
              }))}
              containerClassName="w-full sm:w-48"
            />
            <FilterCombobox
              id="geocercas-filter-3"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTERS.map((value) => ({
                value,
                label: t(`monitoreoIa.geofences.statusFilters.${value}`),
              }))}
              containerClassName="w-full max-sm:col-span-2 sm:w-44"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("monitoreoIa.geofences.searchPlaceholder")}
              icon={<i className="ri-search-line" />}
              containerClassName="w-full flex-1 max-sm:order-first max-sm:col-span-2"
            />
            <ViewToggle
              value={viewMode}
              onChange={setViewMode}
              className="max-sm:col-span-2"
              labels={{
                grid: t("monitoreoIa.geofences.view.grid"),
                list: t("monitoreoIa.geofences.view.list"),
              }}
            />
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-6">
          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-map-pin-5-line"
              title={t("monitoreoIa.geofences.empty")}
              description={t("monitoreoIa.geofences.emptyDescription")}
            />
          ) : viewMode === "grid" ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {paginated.map((geofence) => {
                  const meta = metaOf(geofence.type);
                  const active = isActive(geofence);
                  const readOnly = isSystemManaged(geofence);

                  return (
                    <div
                      key={geofence.id}
                      className={`overflow-hidden rounded-2xl border border-line bg-card transition-colors hover:border-primary-500/40 ${
                        active ? "" : "opacity-60"
                      }`}
                    >
                      <div
                        className={`flex items-start justify-between gap-2 border-b border-line-soft px-4 pb-3 pt-4 ${meta.header}`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${meta.icon}`}
                          >
                            <i className={`${meta.iconClass} text-base`} />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-bold uppercase tracking-wide text-content">
                              {geofence.name}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-content-faint">
                              {t(`monitoreoIa.geofences.types.${geofence.type}`, {
                                defaultValue: geofence.type,
                              })}
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={readOnly}
                          onClick={() => toggleStatus(geofence)}
                          title={t(
                            readOnly
                              ? "monitoreoIa.geofences.systemManaged"
                              : active
                                ? "monitoreoIa.geofences.deactivate"
                                : "monitoreoIa.geofences.activate",
                          )}
                          className={`shrink-0 rounded-md border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                            active
                              ? "border-success/30 bg-success/10 text-success"
                              : "border-line bg-subtle text-content-muted"
                          }`}
                        >
                          {t(
                            `monitoreoIa.geofences.statusFilters.${active ? "active" : "inactive"}`,
                          )}
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-1.5 border-b border-line-soft px-4 py-3">
                        <span
                          className={`rounded-full border px-2.5 py-0.5 text-[10.5px] font-medium ${meta.chip}`}
                        >
                          <i className={`${meta.iconClass} mr-1 text-[10px]`} />
                          {t(`monitoreoIa.geofences.types.${geofence.type}`, {
                            defaultValue: geofence.type,
                          })}
                        </span>
                        <span className="rounded-full border border-line bg-subtle px-2.5 py-0.5 text-[10.5px] font-medium text-content-secondary">
                          {shapeLabel(geofence.shape)}
                        </span>
                        {isDemoRecord(geofence.id) && (
                          <Badge tone="neutral" size="sm">
                            {t("monitoreoIa.demo.badge")}
                          </Badge>
                        )}
                      </div>

                      <dl className="space-y-2 border-b border-line-soft px-4 py-3 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.geofences.radius")}
                          </dt>
                          <dd className="font-semibold text-content">{radiusOf(geofence)}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.geofences.coordinates")}
                          </dt>
                          <dd className="text-[11px] font-medium tabular-nums text-content-secondary">
                            {coordinatesOf(geofence)}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.geofences.createdAt")}
                          </dt>
                          <dd className="font-medium text-content-secondary">
                            {dateOf(geofence.createdAt)}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.geofences.alerts")}
                          </dt>
                          <dd className="font-semibold text-content">{alertsOf(geofence)}</dd>
                        </div>
                      </dl>

                      <div className="px-4 py-3">
                        <div className="flex items-center justify-between rounded-xl border border-line bg-subtle px-3 py-2.5">
                          <span className="flex items-center gap-2 text-xs text-content-secondary">
                            <i className="ri-truck-line text-xs" />
                            {t("monitoreoIa.geofences.unitsInside")}
                          </span>
                          <span className="text-sm font-bold text-content">
                            {unitsInsideOf(geofence)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 px-4 pb-3">
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => setMapGeofence(geofence)}
                        >
                          {t("monitoreoIa.geofences.viewMap")}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          iconOnly
                          disabled={readOnly}
                          aria-label={t("monitoreoIa.actionsCommon.delete")}
                          onClick={() => setDeleteTarget(geofence)}
                          icon={<i className="ri-delete-bin-line" />}
                        />
                      </div>

                      <p className="px-4 pb-4 text-[11px] text-content-faint">
                        {t("monitoreoIa.geofences.updatedAt", {
                          date: dateOf(geofence.updatedAt),
                        })}
                      </p>
                    </div>
                  );
                })}
              </div>

              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-line bg-card">
                <ResponsiveHeader
                  gridClassName={GEOFENCE_GRID}
                  columns={[
                    { id: "geofence", label: t("monitoreoIa.geofences.columns.geofence") },
                    { id: "type", label: t("monitoreoIa.geofences.columns.type") },
                    { id: "shape", label: t("monitoreoIa.geofences.columns.shape") },
                    { id: "units", label: t("monitoreoIa.geofences.columns.units"), center: true },
                    { id: "alerts", label: t("monitoreoIa.geofences.columns.alerts"), center: true },
                    { id: "status", label: t("monitoreoIa.geofences.columns.status"), center: true },
                  ]}
                />

                {paginated.map((geofence) => {
                  const meta = metaOf(geofence.type);
                  const active = isActive(geofence);
                  const readOnly = isSystemManaged(geofence);

                  return (
                    <ResponsiveRow
                      key={geofence.id}
                      gridClassName={GEOFENCE_GRID}
                      className={active ? "hover:bg-subtle" : "opacity-60"}
                      primary={
                      <button
                        type="button"
                        onClick={() => setMapGeofence(geofence)}
                        className="flex min-w-0 items-center gap-2 text-left"
                      >
                        <span
                          className="h-3 w-3 shrink-0 rounded-sm"
                          style={{ background: geofence.color || "rgb(var(--c-content-muted))" }}
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-[13px] font-semibold text-content">
                            {geofence.name}
                          </span>
                          <span className="block truncate text-[10px] text-content-faint">
                            {isDemoRecord(geofence.id)
                              ? `${t("monitoreoIa.demo.badge")} · ${dateOf(geofence.createdAt)}`
                              : dateOf(geofence.createdAt)}
                          </span>
                        </span>
                      </button>
                      }
                      cells={[
                        {
                          id: "type",
                          label: t("monitoreoIa.geofences.columns.type"),
                          node: (
                            <span
                              className={`flex w-fit items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-medium ${meta.chip}`}
                            >
                              <i className={`${meta.iconClass} text-[10px]`} />
                              {t(`monitoreoIa.geofences.types.${geofence.type}`, {
                                defaultValue: geofence.type,
                              })}
                            </span>
                          ),
                        },
                        {
                          id: "shape",
                          label: t("monitoreoIa.geofences.columns.shape"),
                          node: (
                            <span className="text-[11px] text-content-secondary">
                              {shapeLabel(geofence.shape)}
                            </span>
                          ),
                        },
                        {
                          id: "units",
                          label: t("monitoreoIa.geofences.columns.units"),
                          center: true,
                          node: (
                            <span className="text-xs font-bold text-content">
                              {unitsInsideOf(geofence)}
                            </span>
                          ),
                        },
                        {
                          id: "alerts",
                          label: t("monitoreoIa.geofences.columns.alerts"),
                          center: true,
                          node: (
                            <span className="text-xs font-bold text-content">
                              {alertsOf(geofence)}
                            </span>
                          ),
                        },
                        {
                          id: "status",
                          label: t("monitoreoIa.geofences.columns.status"),
                          center: true,
                          node: (
                            <button
                              type="button"
                              disabled={readOnly}
                              onClick={() => toggleStatus(geofence)}
                              title={t(
                                readOnly
                                  ? "monitoreoIa.geofences.systemManaged"
                                  : active
                                    ? "monitoreoIa.geofences.deactivate"
                                    : "monitoreoIa.geofences.activate",
                              )}
                              className="rounded-full disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <SwitchTrack checked={active} size="sm" />
                            </button>
                          ),
                        },
                      ]}
                    />
                  );
                })}
              </div>

              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          )}

          {showSuggestions && (
            <section className="rounded-2xl border border-dashed border-primary-500/50 bg-primary-500/5 p-5">
              <div className="mb-1 flex items-center gap-2">
                <i className="ri-lightbulb-line text-primary-500" />
                <h2 className="text-sm font-bold text-content">
                  {t("monitoreoIa.geofences.suggestionsTitle")}
                </h2>
                <Button
                  variant="ghost"
                  size="xs"
                  className="ml-auto"
                  onClick={() => setShowSuggestions(false)}
                >
                  {t("monitoreoIa.geofences.hideSuggestions")}
                </Button>
              </div>

              <p className="mb-4 text-xs text-content-secondary">
                {t("monitoreoIa.geofences.suggestionsDescription")}
              </p>

              <div className="mb-4 flex w-full flex-col gap-2.5 sm:flex-row sm:items-end">
                <FilterCombobox
                  id="geocercas-suggestion-route"
                  label={t("monitoreoIa.geofences.routeLabel")}
                  value={suggestionRouteId}
                  onChange={setSuggestionRouteId}
                  options={routeOptions}
                  placeholder={t("monitoreoIa.geofences.routePlaceholder")}
                  containerClassName="w-full sm:max-w-sm"
                />
                <Button
                  size="sm"
                  loading={analysisRunning}
                  disabled={!suggestionRouteId || analysisRunning || routesLoading}
                  icon={<i className="ri-sparkling-line" />}
                  onClick={requestAnalysis}
                >
                  {analysisRunning
                    ? t("monitoreoIa.geofences.analyzing")
                    : t("monitoreoIa.geofences.viewSuggestions")}
                </Button>
              </div>

              {corridor ? (
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {corridor.distanceKm !== null ? (
                    <span className="rounded-full border border-line bg-subtle px-2.5 py-1 text-[11px] font-medium text-content-secondary">
                      {t("monitoreoIa.geofences.corridorDistance", {
                        value: corridor.distanceKm,
                      })}
                    </span>
                  ) : null}
                  {corridor.samples !== null ? (
                    <span className="rounded-full border border-line bg-subtle px-2.5 py-1 text-[11px] font-medium text-content-secondary">
                      {t("monitoreoIa.geofences.corridorSamples", {
                        count: corridor.samples,
                      })}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {analysisFailed ? (
                <div className="rounded-xl border border-error/30 bg-error/10 p-4">
                  <p className="text-[13px] font-bold text-error">
                    {t("monitoreoIa.geofences.analysisFailedTitle")}
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-content-secondary">
                    {analysisErrorMessage ?? t("monitoreoIa.geofences.suggestionsFailed")}
                  </p>
                </div>
              ) : analysisRunning ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-line bg-card p-6 text-center">
                  <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
                  <p className="text-[13px] font-bold text-content">
                    {t("monitoreoIa.geofences.analysisRunning")}
                  </p>
                  <p className="max-w-md text-[11px] leading-relaxed text-content-secondary">
                    {jobStart?.detalle ?? t("monitoreoIa.geofences.analysisTakesMinutes")}
                  </p>
                  {analysis?.state === "unknown" ? (
                    <p className="max-w-md text-[11px] leading-relaxed text-warning">
                      {t("monitoreoIa.geofences.unknownJobState", {
                        state: analysis.stateRaw ?? "—",
                      })}
                    </p>
                  ) : null}
                </div>
              ) : analysis?.isReady ? (
                analysis.suggestions.length === 0 ? (
                  <EmptyState
                    icon="ri-lightbulb-line"
                    title={t("monitoreoIa.geofences.noSuggestions")}
                    description={t("monitoreoIa.geofences.noSuggestionsDescription")}
                  />
                ) : (
                  <>
                    {visibleSuggestions.length === 0 ? (
                      <p className="rounded-xl border border-line bg-card p-4 text-[11px] leading-relaxed text-content-secondary">
                        {t("monitoreoIa.geofences.allSuggestionsDismissed")}
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {visibleSuggestions.map((suggestion) => {
                          const meta = metaOf(suggestion.type);

                          return (
                            <li
                              key={suggestion.id}
                              className="flex flex-col gap-3 rounded-xl border border-line bg-card p-3.5 sm:flex-row sm:items-start"
                            >
                              <div className="flex min-w-0 flex-1 items-start gap-2.5">
                                <span
                                  className="mt-1 h-3 w-3 shrink-0 rounded-sm"
                                  style={{ backgroundColor: suggestion.color }}
                                />

                                <div className="min-w-0">
                                  <p className="text-[13px] font-bold text-content">
                                    {suggestion.name}
                                  </p>
                                  <p className="mt-0.5 text-[11px] leading-relaxed text-content-secondary">
                                    {suggestion.reason}
                                  </p>

                                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                    {suggestion.type ? (
                                      <span
                                        className={`flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${meta.chip}`}
                                      >
                                        <i className={`${meta.iconClass} text-[9px]`} />
                                        {t(
                                          `monitoreoIa.geofences.types.${suggestion.type}`,
                                          { defaultValue: suggestion.type },
                                        )}
                                      </span>
                                    ) : null}

                                    {suggestion.confidenceRaw ? (
                                      <span
                                        className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${
                                          suggestion.confidence
                                            ? CONFIDENCE_CHIP[suggestion.confidence]
                                            : "border-line bg-subtle text-content-muted"
                                        }`}
                                      >
                                        {t("monitoreoIa.geofences.confidenceLevel", {
                                          value: suggestion.confidence
                                            ? t(
                                                `monitoreoIa.geofences.confidences.${suggestion.confidence}`,
                                              )
                                            : suggestion.confidenceRaw,
                                        })}
                                      </span>
                                    ) : null}

                                    {suggestion.origin ? (
                                      <span className="rounded-md border border-line bg-subtle px-1.5 py-0.5 text-[10px] font-medium text-content-muted">
                                        {t(
                                          `monitoreoIa.geofences.origins.${suggestion.origin}`,
                                          { defaultValue: suggestion.origin },
                                        )}
                                      </span>
                                    ) : null}

                                    {suggestion.radiusMeters !== null ? (
                                      <span className="text-[10px] text-content-faint">
                                        {t("monitoreoIa.geofences.radiusValue", {
                                          meters: suggestion.radiusMeters,
                                        })}
                                      </span>
                                    ) : null}

                                    {suggestion.distanceToRouteKm !== null ? (
                                      <span className="text-[10px] text-content-faint">
                                        {t("monitoreoIa.geofences.distanceToRoute", {
                                          value: suggestion.distanceToRouteKm,
                                        })}
                                      </span>
                                    ) : null}
                                  </div>

                                  {suggestion.sources.length > 0 ? (
                                    <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                                      {suggestion.sources.map((source) => (
                                        <li key={source.url}>
                                          <a
                                            href={source.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[10px] text-primary-500 underline decoration-dotted underline-offset-2 hover:text-primary-400"
                                          >
                                            {source.title}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : null}
                                </div>
                              </div>

                              <div className="flex shrink-0 items-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => acceptSuggestion(suggestion)}
                                >
                                  {t("monitoreoIa.geofences.accept")}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setDismissed((prev) => [...prev, suggestion.id])
                                  }
                                >
                                  {t("monitoreoIa.geofences.dismiss")}
                                </Button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {analysis.sources.length > 0 ? (
                      <div className="mt-4 rounded-xl border border-line bg-card p-3.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-content-secondary">
                            {t("monitoreoIa.geofences.sourcesTitle")}
                          </p>
                          {analysis.searches !== null ? (
                            <span className="text-[10px] text-content-faint">
                              {t("monitoreoIa.geofences.webSearches", {
                                count: analysis.searches,
                              })}
                            </span>
                          ) : null}
                        </div>
                        <ul className="mt-2 space-y-1">
                          {analysis.sources.map((source) => (
                            <li key={source.url}>
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] text-primary-500 underline decoration-dotted underline-offset-2 hover:text-primary-400"
                              >
                                {source.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </>
                )
              ) : (
                <p className="rounded-xl border border-line bg-card p-4 text-[11px] leading-relaxed text-content-secondary">
                  {suggestionRouteId
                    ? t("monitoreoIa.geofences.readyToAnalyze")
                    : t("monitoreoIa.geofences.pickRoute")}
                </p>
              )}
            </section>
          )}
        </div>
      </div>

      {mapGeofence ? (
        <GeofenceMapModal
          geofence={mapGeofence}
          unitsInside={unitsInsideOf(mapGeofence)}
          onClose={() => setMapGeofence(null)}
        />
      ) : null}

      <GeofenceModal
        isOpen={showCreateModal}
        isEdit={false}
        hideNotificationFields
        loadingSubmit={loadingSubmit}
        form={form}
        shape={shape}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        editGeofence={null}
        polygonPoints={polygonPoints}
        setPolygonPoints={setPolygonPoints}
        linearPoints={linearPoints}
        setLinearPoints={setLinearPoints}
        routeDistance={routeDistance}
        estimatedTime={estimatedTime}
        polygonArea={polygonArea}
        defaultCircularCoordinates={defaultCircularCoordinates}
        hasRealChanges={hasRealChanges}
        onAddressSelect={handleAddressSelect}
        onLinearPointsChange={handleLinearPointsChange}
        onAxisCalculated={handleAxisCalculated}
        onBufferChange={handleBufferChange}
        linearAddresses={linearAddresses}
        onLinearAddressEdit={handleLinearAddressEdit}
        onRouteCalculated={handleRouteCalculated}
        onSubmit={submitGeofence}
        onClose={closeCreateModal}
      />

      {deleteTarget ? (
        <ConfirmDeleteModal
          title={t("monitoreoIa.geofences.deleteTitle")}
          name={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => removeGeofence(deleteTarget)}
        />
      ) : null}
    </div>
  );
}
